import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

const getTriggerCharMatcher =
  (char) =>
  ($position, allowSpaces = false) => {
    const regexp = allowSpaces
      ? new RegExp(
          `(^|\\s)${char}([\\w-\\+]+(\\s[\\w-\\+]+){0,1}\\s?[\\w-\\+]*)$`
        )
      : new RegExp(`(^|\\s)${char}([\\w-\\+]+)$`);

    const text = $position.doc.textBetween(
      $position.before(),
      $position.pos,
      "\0",
      "\0"
    );
    const match = text.match(regexp);

    if (match) {
      const matchText = match[0].substring(match[1].length, match[0].length);

      const to = $position.pos;
      const from = to - matchText.length;

      if ($position.doc.resolve(from)?.parent?.type?.isTextblock === false) {
        return undefined;
      }

      return { range: { from, to }, text: match[2] };
    }

    return undefined;
  };

const getNewState = () => ({
  active: false,
  range: {},
  text: "",
});

export const mentionPluginKey = new PluginKey("mention");

export const mentionPlugin = ({
  triggerChar = "@",
  allowSpaces = false,
  debounceTime = 500,
  onEnter = () => false,
  onExit = () => false,
  onChange = () => false,
  onDebouncedChange = () => false,
}) => {
  const matcher = getTriggerCharMatcher(triggerChar);
  let debounceTimeoutId = null;

  return new Plugin({
    key: mentionPluginKey,
    view() {
      return {
        update: (view, prevState) => {
          const prev = this.key.getState(prevState);
          const next = this.key.getState(view.state);
          const node = this.props.getNode(view);

          // See how the state changed
          const started = !prev.active && next.active;
          const stopped = prev.active && !next.active;
          const changed = !started && !stopped && prev.text !== next.text;

          // Trigger the hooks when necessary

          if (started) {
            onEnter({
              view,
              node,
              range: next.range,
              text: next.text,
            });
          }

          if (stopped) {
            onExit({
              view,
              node,
              range: prev.range,
              text: prev.text,
            });
          }

          if (started || changed) {
            onChange({
              view,
              node,
              range: next.range,
              text: next.text,
            });

            if (debounceTimeoutId) {
              clearTimeout(debounceTimeoutId);
            }

            debounceTimeoutId = setTimeout(() => {
              if (next.active) {
                onDebouncedChange({
                  view,
                  node,
                  range: next.range,
                  text: next.text,
                });
              }
            }, debounceTime);
          }
        },
      };
    },
    state: {
      init() {
        return getNewState();
      },
      apply(tr) {
        const newState = getNewState();
        const { selection } = tr;

        if (selection.from !== selection.to) {
          return newState;
        }

        const $position = selection.$from;
        const match = matcher($position, allowSpaces);

        if (match) {
          newState.active = true;
          newState.range = match.range;
          newState.text = match.text;
        }

        return newState;
      },
    },
    props: {
      getNode(view) {
        return view
          ?.domAtPos(view.state.selection.$from.pos)
          ?.node?.querySelector?.(
            'span[data-mark][data-type="mention-typing"]'
          );
      },
      // handleKeyDown(view, event) {
      //   const { active, range, text } = this.getState(view.state);
      //   const node = this.props.getNode(view);

      //   if (!active) return false;

      //   return onKeyDown(event, { view, node, range, text }) || false;
      // },
      decorations(editorState) {
        const { active, range } = this.getState(editorState);

        if (!active) return null;

        return DecorationSet.create(editorState.doc, [
          Decoration.inline(range.from, range.to, {
            nodeName: "span",
            "data-mark": "",
            "data-type": "mention-typing",
          }),
        ]);
      },
    },
  });
};
